import type { ConditionalDisplayConfig } from "@repo/shared";
import { Label } from "@/components/ui/label";
import type { ColumnConfig, LayoutConfig } from "@/lib/types/flow";
import { TabsBlockRenderer } from "../tabs-block-renderer";
import {
  type ConditionEvaluationData,
  evaluateCondition,
  getColumnClasses,
  renderColumnHeader,
} from "./flow-wizard-utils";

type PageItem = {
  type: "layout" | "component";
  id: string;
  order: number;
  data: any;
};

type WizardPageRendererProps = {
  layouts: any[];
  components: any[];
  conditionData: ConditionEvaluationData;
  renderComponent: (component: any) => React.ReactNode;
  showValidationErrors?: boolean;
  missingTabKeys?: Set<string>;
};

/**
 * Renders a flow page's layouts and standalone components in order.
 * Handles conditional display, two-column layouts, and tabs_block layouts.
 *
 * This component eliminates duplication between multi-goal global pages,
 * single-goal pages, and goal pages.
 */
export function WizardPageRenderer({
  layouts,
  components,
  conditionData,
  renderComponent,
  showValidationErrors,
  missingTabKeys,
}: WizardPageRendererProps) {
  // Filter standalone components (not in a layout)
  const standaloneComponents = components.filter((c: any) => !c.flowPageLayoutId);

  // Combine layouts and standalone components into ordered array
  const pageItems: PageItem[] = [
    ...layouts.map((l: any) => ({
      type: "layout" as const,
      id: l.id,
      order: l.order,
      data: l,
    })),
    ...standaloneComponents.map((c: any) => ({
      type: "component" as const,
      id: c.id,
      order: c.order,
      data: c,
    })),
  ].sort((a, b) => a.order - b.order);

  return (
    <>
      {pageItems.map((item) => {
        if (item.type === "layout") {
          return (
            <LayoutRenderer
              key={item.id}
              layout={item.data}
              conditionData={conditionData}
              renderComponent={renderComponent}
              showValidationErrors={showValidationErrors}
              missingTabKeys={missingTabKeys}
            />
          );
        } else {
          return (
            <StandaloneComponentRenderer
              key={item.id}
              component={item.data}
              conditionData={conditionData}
              renderComponent={renderComponent}
            />
          );
        }
      })}
    </>
  );
}

type LayoutRendererProps = {
  layout: any;
  conditionData: ConditionEvaluationData;
  renderComponent: (component: any) => React.ReactNode;
  showValidationErrors?: boolean;
  missingTabKeys?: Set<string>;
};

function LayoutRenderer({
  layout,
  conditionData,
  renderComponent,
  showValidationErrors,
  missingTabKeys,
}: LayoutRendererProps) {
  // Parse layout config for conditional display and column styling
  let layoutConditionalDisplay: ConditionalDisplayConfig | null = null;
  let leftColumnConfig: ColumnConfig | undefined;
  let rightColumnConfig: ColumnConfig | undefined;
  try {
    const config: LayoutConfig | null = layout.config ? JSON.parse(layout.config) : null;
    layoutConditionalDisplay = config?.conditionalDisplay || null;
    leftColumnConfig = config?.leftColumn;
    rightColumnConfig = config?.rightColumn;
  } catch {
    // Invalid config, ignore
  }

  // Check if layout should be shown
  const shouldShowLayout = evaluateCondition(layoutConditionalDisplay, conditionData);
  if (!shouldShowLayout) return null;

  // Check if this is a tabs_block layout
  if (layout.layoutType === "tabs_block") {
    return (
      <TabsBlockRenderer
        layoutId={layout.id}
        layoutConfig={layout.config}
        components={layout.components}
        renderComponent={(component) => (
          // QL-119 — same 8px label -> control rhythm as ComponentWithConditionalDisplay;
          // a bare fragment here left tabbed fields with no gap at all.
          <div className="space-y-2">
            {component.componentType === "field" && component.flowField && (
              <Label htmlFor={component.flowField.id}>
                {component.flowField.name}
                {component.flowField.isRequired && <span className="text-destructive ml-1">*</span>}
              </Label>
            )}
            {renderComponent(component)}
          </div>
        )}
        conditionData={conditionData}
        showValidationErrors={showValidationErrors}
        missingTabKeys={missingTabKeys}
      />
    );
  }

  // Two-column layout
  const leftComponents = layout.components.filter((c: any) => c.columnPosition === "left");
  const rightComponents = layout.components.filter((c: any) => c.columnPosition === "right");

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Left column */}
      <div className={getColumnClasses(leftColumnConfig)}>
        {renderColumnHeader(leftColumnConfig)}
        {leftComponents.map((component: any) => (
          <ComponentWithConditionalDisplay
            key={component.id}
            component={component}
            conditionData={conditionData}
            renderComponent={renderComponent}
          />
        ))}
      </div>

      {/* Right column */}
      <div className={getColumnClasses(rightColumnConfig)}>
        {renderColumnHeader(rightColumnConfig)}
        {rightComponents.map((component: any) => (
          <ComponentWithConditionalDisplay
            key={component.id}
            component={component}
            conditionData={conditionData}
            renderComponent={renderComponent}
          />
        ))}
      </div>
    </div>
  );
}

type StandaloneComponentRendererProps = {
  component: any;
  conditionData: ConditionEvaluationData;
  renderComponent: (component: any) => React.ReactNode;
};

function StandaloneComponentRenderer({
  component,
  conditionData,
  renderComponent,
}: StandaloneComponentRendererProps) {
  return (
    <ComponentWithConditionalDisplay
      component={component}
      conditionData={conditionData}
      renderComponent={renderComponent}
    />
  );
}

type ComponentWithConditionalDisplayProps = {
  component: any;
  conditionData: ConditionEvaluationData;
  renderComponent: (component: any) => React.ReactNode;
};

function ComponentWithConditionalDisplay({
  component,
  conditionData,
  renderComponent,
}: ComponentWithConditionalDisplayProps) {
  // Parse config to check conditional display
  let conditionalDisplay: ConditionalDisplayConfig | null = null;
  try {
    const config = component.config ? JSON.parse(component.config) : null;
    conditionalDisplay = config?.conditionalDisplay || null;
  } catch {
    // Invalid config, ignore
  }

  // Check if component should be shown
  const shouldShow = evaluateCondition(conditionalDisplay, conditionData);
  if (!shouldShow) return null;

  return (
    <div className="space-y-2">
      {component.componentType === "field" && component.flowField && (
        <Label htmlFor={component.flowField.id}>
          {component.flowField.name}
          {component.flowField.isRequired && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      {renderComponent(component)}
    </div>
  );
}
